import { useId } from "react";

import type { PhaseId } from "../domain/cycle";
import type { CycleRing as Ring, RingArc } from "../domain/cycleRing";
import { PANEL_WORDS, PHASE_WORDS } from "../domain/cycleWords";
import { Chip } from "./Chip";
import "./CycleRing.css";

// The drawing's own coordinates (SNIPPETS §50). The SVG is drawn 1:1 at Normal on a 360 px phone
// and only ever scales up, so the labels inside it never fall under the 0.8rem floor.
const W = 288;
const H = 224;
const CX = 144;
const CY = 112;
const R = 80;
const GAP = 4;
const CIRC = 2 * Math.PI * R;

const at = (frac: number, rad: number): [number, number] => {
  const t = frac * 2 * Math.PI;
  return [CX + rad * Math.sin(t), CY - rad * Math.cos(t)];
};

/** An arc's start and length along the circle, in px, less half the gap at each end. */
function span(a: RingArc, len: number): { start: number; length: number } {
  const start = ((a.from - 1) / len) * CIRC + GAP / 2;
  const end = (a.to / len) * CIRC - GAP / 2;
  return { start, length: Math.max(0, end - start) };
}

function Arc({
  start,
  length,
  className,
  onClick,
}: {
  start: number;
  length: number;
  className: string;
  onClick?: () => void;
}) {
  if (length <= 0.5) return null;
  return (
    <circle
      className={className}
      onClick={onClick}
      cx={CX}
      cy={CY}
      r={R}
      fill="none"
      strokeDasharray={`${length.toFixed(2)} ${CIRC.toFixed(2)}`}
      strokeDashoffset={(-start).toFixed(2)}
      transform={`rotate(-90 ${CX} ${CY})`}
    />
  );
}

/**
 * The cycle as a circle (ADR-059, SNIPPETS §50): day 1 at the top, clockwise, one arc per phase,
 * the days to today drawn over the days to come, the ovulation estimate as a tick and today as the
 * brand marker, with the current phase and the next in the centre. Every figure and word comes
 * from `domain/cycleRing.ts`; this only draws.
 *
 * The SVG is one image with an aria-label; the arcs can be tapped, and the chips under the ring
 * are the accessible way to pick a phase. With no phases there are no chips and nothing to tap.
 */
export function CycleRing({
  ring,
  selected,
  onSelect,
}: {
  ring: Ring;
  /** the phase the panel shows; its arc is drawn widest */
  selected: PhaseId | null;
  onSelect?: (id: PhaseId) => void;
}) {
  const maskId = `k-cyclering-${useId().replace(/:/g, "")}`;
  const { len, arcs, todayFrac } = ring;
  const todayPos = todayFrac * CIRC;
  const weight = (a: RingArc) =>
    a.id === selected ? " is-selected" : a.id === ring.current && ring.phased ? " is-current" : "";
  const pick = (a: RingArc) => (ring.phased && onSelect && a.id !== "rest" ? () => onSelect(a.id as PhaseId) : undefined);

  const [mx, my] = at(todayFrac, R);
  const ov = ring.ovulationDay;
  const ovFrac = ov !== null ? (ov - 0.5) / len : null;
  const ovLabel = ovFrac !== null ? at(ovFrac, R + 20) : null;
  const anchor = ovLabel ? (ovLabel[0] > CX + 20 ? "start" : ovLabel[0] < CX - 20 ? "end" : "middle") : "middle";

  return (
    <div className="k-cyclering">
      <div className="k-cyclering__stack">
        <svg className="k-cyclering__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ring.ariaLabel}>
          <defs>
            {/* A luminance mask: the sweep reveals the days to today, clockwise from day 1. */}
            <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={W} height={H}>
              <circle
                className="k-cyclering__sweep"
                cx={CX}
                cy={CY}
                r={R}
                fill="none"
                stroke="white"
                strokeWidth={28}
                strokeDasharray={`${todayPos.toFixed(2)} ${(CIRC * 2).toFixed(2)}`}
                transform={`rotate(-90 ${CX} ${CY})`}
                style={{ ["--k-sweep" as string]: todayPos.toFixed(2) }}
              />
            </mask>
          </defs>

          {arcs.map((a) => {
            const s = span(a, len);
            return <Arc key={`to-${a.id}`} {...s} className={`k-cyclering__arc k-cyclering__arc--${a.id}${weight(a)}`} />;
          })}
          <g mask={`url(#${maskId})`}>
            {arcs.map((a) => {
              const s = span(a, len);
              const length = Math.min(s.start + s.length, todayPos) - s.start;
              return (
                <Arc
                  key={`past-${a.id}`}
                  start={s.start}
                  length={length}
                  className={`k-cyclering__arc k-cyclering__arc--past k-cyclering__arc--past-${a.id === "menstrual" ? "period" : "day"}${weight(a)}`}
                />
              );
            })}
          </g>

          {ovFrac !== null && ovLabel ? (
            <>
              <line
                className="k-cyclering__tick"
                x1={at(ovFrac, R - 11)[0]}
                y1={at(ovFrac, R - 11)[1]}
                x2={at(ovFrac, R + 11)[0]}
                y2={at(ovFrac, R + 11)[1]}
              />
              <text className="k-cyclering__label" x={ovLabel[0]} y={ovLabel[1] + 8} textAnchor={anchor}>
                ovulation ~{ov}
              </text>
            </>
          ) : null}
          <text className="k-cyclering__label" x={CX + 6} y={16} textAnchor="start">
            1
          </text>
          <text className="k-cyclering__label" x={CX - 6} y={16} textAnchor="end">
            {len}
          </text>

          {arcs.map((a) => {
            const tap = pick(a);
            if (!tap) return null;
            const s = span(a, len);
            return <Arc key={`hit-${a.id}`} {...s} className="k-cyclering__hit" onClick={tap} />;
          })}

          <circle className="k-cyclering__halo" cx={mx} cy={my} r={13} />
          <circle className="k-cyclering__marker" cx={mx} cy={my} r={7} />
        </svg>
        <div className="k-cyclering__centre" aria-hidden="true">
          <span className="k-cyclering__title">{ring.centre.title}</span>
          {ring.centre.lines.map((l) => (
            <span key={l} className="k-cyclering__next">
              {l}
            </span>
          ))}
        </div>
      </div>

      {ring.phased && onSelect ? (
        <div className="k-cyclering__chips" role="group" aria-label={PANEL_WORDS.chips}>
          {arcs.map((a) =>
            a.id === "rest" ? null : (
              <Chip key={a.id} selected={a.id === selected} onClick={() => onSelect(a.id as PhaseId)}>
                {PHASE_WORDS[a.id].title}
              </Chip>
            )
          )}
        </div>
      ) : null}
    </div>
  );
}
